import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/routes';
import CustomerTopNav from '../../components/customerPortal/CustomerTopNav';
import CustomerFooter from '../../components/customerPortal/CustomerFooter';
import { VivaClubGame } from '../../components/games/VivaTaps/VivaClubGame';
import LivesPurchaseModal from '../../components/games/LivesPurchaseModal';
import { submitGameScore, GameScoreRequest, getGames, Game, getCustomerGameStats, normalizeGamePricing, startGame } from '../../api/gameScore';
import { currencySymbol } from '../../utils/currency';
import SecureStorage from '../../utils/secureStorage';
import '../../styles/customerPortal.css';

// Helper function to ensure HTTPS URLs (fix mixed content issue)
const ensureHttps = (url: string | null | undefined): string | null => {
    if (!url) return null;
    // If URL starts with http:// (not https://), replace it with https://
    if (url.startsWith('http://')) {
        return url.replace('http://', 'https://');
    }
    return url;
};

// Game score interface
interface GameScore {
    score: number;
    level: number;
    timestamp: string;
}

const Games = () => {
    const [games, setGames] = useState<Game[]>([]);
    const [selectedGame, setSelectedGame] = useState<Game | null>(null);
    const [gameLoaded, setGameLoaded] = useState(false);
    const [currentScore, setCurrentScore] = useState(0);
    const [bestScore, setBestScore] = useState(0);
    const [totalScore, setTotalScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dailyPlaysRemaining, setDailyPlaysRemaining] = useState(0);
    const [statsLoaded, setStatsLoaded] = useState(false);
    const [currentActivityId, setCurrentActivityId] = useState<string | null>(null);
    const [isStarting, setIsStarting] = useState(false);
    const [isSubmittingScore, setIsSubmittingScore] = useState(false);
    const [isInitialGameLoad, setIsInitialGameLoad] = useState(false); // Track if game just loaded from "Start Game" button
    
    // Lives purchase modal state
    const [showLivesPurchaseModal, setShowLivesPurchaseModal] = useState(false);
    const [selectedGameForPurchase, setSelectedGameForPurchase] = useState<Game | null>(null);

    const [customerStatsByGame, setCustomerStatsByGame] = useState<Record<string, { dailyPlaysRemaining: number; totalPlaysToday: number; totalScore?: number; purchasedLives?: number; totalDailyLimit?: number }>>({});

    const navigate = useNavigate();

    useEffect(() => {
        const initializePage = async () => {
            try {
                // Early auth check: if no valid customer token, redirect to customer sign-in with return path
                const token = SecureStorage.getCustomerToken();
                if (!token) {
                    // Preserve intended destination so user returns after sign-in
                    navigate(`${PATHS.CUSTOMER_SIGNIN}?redirect=${encodeURIComponent(PATHS.CUSTOMER_GAMES)}`);
                    return; // Abort further loading
                }
                const gamesList = await fetchGames();
                await fetchCustomerStats();
                await fetchAllGameStats(gamesList);
            } catch (error) {
                console.error('Error initializing games page:', error);
                setError('Failed to load games');
            } finally {
                setLoading(false);
            }
        };
        initializePage();
    }, []);

    // Fetch customer game statistics (total score only)
    const fetchCustomerStats = async () => {
        const token = SecureStorage.getCustomerToken();
        if (!token) {
            console.log('❌ No token - skipping total score fetch');
            return;
        }

        try {
            console.log('📊 Fetching customer total score...');
            const response = await getCustomerGameStats();
            console.log('📊 Total score response:', response);
            
            if (response.success && response.data) {
                setTotalScore(response.data.totalScore || 0);
                console.log('✅ Total score from database:', response.data.totalScore);
            }
        } catch (error: any) {
            console.error('Failed to fetch customer total score:', error);
            // Don't show error to user - stats is optional
        }
    };

    // Fetch remaining plays for each game individually
    const fetchAllGameStats = async (gamesList: Game[]) => {
        const token = SecureStorage.getCustomerToken();
        if (!token) {
            console.log('❌ No token - skipping per-game stats fetch');
            setStatsLoaded(true);
            return;
        }

        try {
            const statsByGame: Record<string, { dailyPlaysRemaining: number; totalPlaysToday: number; totalScore?: number; purchasedLives?: number; totalDailyLimit?: number }> = {};

            // Fetch stats for each game individually
            for (const game of gamesList) {
                try {
                    console.log(`📊 Fetching stats for game: ${game.name}`);
                    const response = await getCustomerGameStats(game.name);
                    
                    if (response.success && response.data) {
                        // USE EXACT SAME DATE LOGIC AS BACKEND - NO MORE GUESSING!
                        // Backend uses: const today = new Date().toISOString().split('T')[0];
                        const backendTodayStr = new Date().toISOString().split('T')[0]; // EXACT same as backend line 76
                        
                        console.log(`📊 Stats for ${game.name}:`, response.data);
                        console.log(`🌐 Backend today (UTC): ${backendTodayStr}`);
                        console.log(`📅 Recent games:`, response.data.recentGames);
                        
                        // Count plays using EXACT same date logic as backend
                        const totalPlaysToday = (response.data.recentGames || []).filter((rg) => {
                            // rg.playDate is stored as YYYY-MM-DD string from backend
                            const match = rg.playDate === backendTodayStr;
                            console.log(`🎮 Game: ${rg.playDate} vs backend today: ${backendTodayStr} = ${match}`);
                            return match;
                        }).length;

                        // Prefer backend-provided dailyPlaysRemaining (it includes purchased lives)
                        let remaining: number;
                        if (typeof response.data.dailyPlaysRemaining === 'number') {
                            remaining = response.data.dailyPlaysRemaining;
                            console.log(`🎯 ${game.name}: backend reports remaining ${remaining} (includes purchased lives)`);
                            statsByGame[game.name] = {
                                dailyPlaysRemaining: remaining,
                                totalPlaysToday: typeof response.data.totalDailyLimit === 'number' ? (response.data.totalDailyLimit - remaining) : totalPlaysToday,
                                totalScore: response.data.totalScore,
                                purchasedLives: response.data.purchasedLives || 0,
                                totalDailyLimit: response.data.totalDailyLimit || game.maxPlayPerDay
                            };
                        } else {
                            remaining = Math.max(0, game.maxPlayPerDay - totalPlaysToday);
                            console.log(`🎯 ${game.name}: played ${totalPlaysToday}/${game.maxPlayPerDay} today, ${remaining} remaining`);
                            statsByGame[game.name] = {
                                dailyPlaysRemaining: remaining,
                                totalPlaysToday: game.maxPlayPerDay - remaining,
                                totalScore: response.data.totalScore
                            };
                        }
                        console.log(`✅ Game ${game.name}: ${remaining} plays remaining (from backend validation)`);
                    }
                } catch (error) {
                    console.warn(`Failed to fetch stats for game ${game.name}:`, error);
                    // On error, don't make assumptions - mark as unknown
                    // Better to show "..." than misleading "3" when user might have 0
                    statsByGame[game.name] = { 
                        dailyPlaysRemaining: game.maxPlayPerDay, // Safe default for error case
                        totalPlaysToday: 0 
                    };
                    console.log(`⚠️ Error fetching ${game.name} stats, using safe default: ${game.maxPlayPerDay} plays`);
                }
            }

            setCustomerStatsByGame(statsByGame);
            console.log('📊 All game stats loaded:', statsByGame);
        } catch (error) {
            console.error('Error fetching all game stats:', error);
        } finally {
            setStatsLoaded(true);
        }
    };

    // Removed auto-reset useEffect - it was causing incorrect "3/3" displays
    // Users should refresh page after reset time to get updated stats from server

    const fetchGames = async () => {
        try {
            const response = await getGames();
            setGames(response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching games:', error);
            setError('Failed to load games');
            throw error;
        }
    };

    const handleGameScore = async (gameData: GameScore) => {
        if (!selectedGame) return;
        
        console.log('🔍 handleGameScore called with:', gameData);
        console.log('🎮 Selected game:', selectedGame.name);
        console.log('🆔 Current activity ID at score submission:', currentActivityId);
        
        try {
            setCurrentScore(gameData.score);
            
            // Debug: Always log score data
            console.log('🔍 Score data received:', {
                score: gameData.score,
                type: typeof gameData.score,
                level: gameData.level,
                timestamp: gameData.timestamp
            });
            
            // Allow zero scores - only reject negative or non-numeric scores
            if (typeof gameData.score !== 'number' || gameData.score < 0) {
                console.log('❌ Ignoring invalid score:', gameData.score, 'type:', typeof gameData.score);
                return;
            }
            
            console.log('✅ Valid score received:', gameData.score);
            
            // Get token once for both operations
            const token = SecureStorage.getCustomerToken();
            const isTokenValid = SecureStorage.isCustomerTokenValid();
            console.log('🔐 Auth Debug:', {
                hasToken: !!token,
                isTokenValid: isTokenValid,
                tokenPreview: token ? token.substring(0, 30) + '...' : 'null',
                localStorageKeys: Object.keys(localStorage).filter(k => k.includes('token') || k.includes('Token'))
            });
            
            // Update best score (only if authenticated)
            if (token && gameData.score > bestScore) {
                setBestScore(gameData.score);
                console.log('🏆 New best score:', gameData.score);
            }

            // Submit score via API (only if authenticated and valid score)
            if (token) {
                console.log('📤 Preparing to submit score to API...');
                setIsSubmittingScore(true); // Prevent new games from starting during submission
                
                const gameScoreRequest: GameScoreRequest = {
                    score: gameData.score,
                    level: gameData.level || 1,
                    gameName: selectedGame.name,
                    playDuration: 0,
                    gameData: {},
                    activityId: currentActivityId || undefined
                };

                try {
                    console.log('🚀 Calling submitGameScore API with:', gameScoreRequest);
                    console.log('🆔 Current activity ID:', currentActivityId);
                    const response = await submitGameScore(gameScoreRequest);
                    console.log('✅ API Response:', response);
                    
                    if (response.success) {
                        console.log(`🎉 Game completed! Score: ${gameData.score}, New high score: ${response.data.isNewHighScore}`);
                        
                        // Update daily plays remaining from API response
                        setDailyPlaysRemaining(response.data.dailyPlaysRemaining);
                        // Update per-game map as well so cards update immediately
                        setCustomerStatsByGame((prev) => ({
                            ...prev,
                            [selectedGame.name]: {
                                ...(prev[selectedGame.name] || { totalPlaysToday: 0 }),
                                dailyPlaysRemaining: response.data.dailyPlaysRemaining,
                            }
                        }));
                        console.log('📊 Daily plays remaining:', response.data.dailyPlaysRemaining);
                        
                        // Refresh total score from database
                        await fetchCustomerStats();
                        
                        // Clear activity ID so next game starts fresh
                        console.log('🧹🧹🧹 CLEARING ACTIVITY ID AFTER SUCCESSFUL SCORE SUBMISSION 🧹🧹🧹');
                        console.log('🆔 Activity ID before clear:', currentActivityId);
                        setCurrentActivityId(null);
                        console.log('🆔 Activity ID should now be null');
                    }
                } catch (error: any) {
                    console.error('❌ Error submitting score:', error);
                    console.error('❌ Error details:', error?.response?.data || error?.message);
                    
                    // Clear activity ID on any error to ensure fresh start next time
                    console.log('🧹 Clearing activity ID after error:', error?.message);
                    setCurrentActivityId(null);
                    
                    // Handle specific error types with user-friendly messages
                    if (error?.response?.status === 429) {
                        const errorMessage = error?.response?.data?.message || 'Too many requests';
                        console.log('⚠️ 429 error received:', errorMessage);
                        
                        // Update local state to reflect backend's exact count
                        const errorData = error?.response?.data?.data;
                        let actualRemaining = 0;
                        
                        if (errorData?.maxPlays && typeof errorData?.currentPlays === 'number') {
                            actualRemaining = Math.max(0, errorData.maxPlays - errorData.currentPlays);
                            console.log(`🎯 Backend exact count: ${errorData.currentPlays}/${errorData.maxPlays}, remaining: ${actualRemaining}`);
                        }
                        
                        setDailyPlaysRemaining(actualRemaining);
                        setCustomerStatsByGame((prev) => ({
                            ...prev,
                            [selectedGame.name]: {
                                ...(prev[selectedGame.name] || { totalPlaysToday: 0 }),
                                dailyPlaysRemaining: actualRemaining,
                                totalPlaysToday: errorData?.currentPlays || 0,
                            }
                        }));
                        
                        if (errorMessage.includes('Daily play limit reached')) {
                            setError('You have reached your daily play limit. Try again tomorrow!');
                        } else {
                            setError('Please wait a moment before playing again.');
                        }
                    } else if (error?.message === 'Customer authentication required') {
                        setError('Please sign in to save your score.');
                    } else if (error?.message !== 'Invalid score payload') {
                        setError('Failed to save score. Please try again.');
                    }
                } finally {
                    // Always clear the submitting flag when score submission is done
                    setIsSubmittingScore(false);
                }
            } else {
                console.log('User not authenticated, score not submitted');
                // Clear submitting flag even if not authenticated
                setIsSubmittingScore(false);
            }
            
        } catch (error: any) {
            console.error('Error processing game score:', error);
            
            // Clear activity ID on any error to ensure fresh start next time
            console.log('🧹 Clearing activity ID after outer error:', error?.message);
            setCurrentActivityId(null);
            
            if (error?.message !== 'Invalid score payload' && error?.message !== 'Customer authentication required') {
                setError(error.message || 'Failed to save game score');
            }
        } finally {
            // Always clear the submitting flag, even in outer catch
            setIsSubmittingScore(false);
        }
    };

    // Function to register a play without loading the game (for Play Again)
    const handleGamePlayStart = async (): Promise<void> => {
        console.log('🎮 Registering play start for loaded game (Play Again clicked)');
        
        // If this is the initial game load (user just clicked "Start Game" button),
        // don't consume another life - it was already consumed in handleGameStart
        if (isInitialGameLoad) {
            console.log('✅ Initial game load detected - life already consumed in handleGameStart, skipping API call');
            setIsInitialGameLoad(false); // Reset flag after first start
            return;
        }
        
        // This is a genuine "Play Again" - consume a life
        console.log('🔄 This is Play Again - consuming 1 life');
        
        if (dailyPlaysRemaining <= 0) {
            console.log('❌ No plays remaining today!');
            setError('No plays remaining today!');
            return;
        }

        if (isStarting || isSubmittingScore) {
            console.log('❌ Cannot start - operation in progress');
            return;
        }

        setIsStarting(true);
        
        // Clear previous activity ID to start a fresh play session
        console.log('🧹 Clearing previous activity ID before new play');
        setCurrentActivityId(null);

        try {
            console.log('🚀 Calling startGame API to consume 1 life for Play Again');
            const res = await startGame(selectedGame!.name);
            if (res && res.data) {
                setDailyPlaysRemaining(res.data.dailyPlaysRemaining);
                console.log('🆔 Setting new activity ID for Play Again:', res.data.activityId);
                console.log('� Lives remaining after Play Again:', res.data.dailyPlaysRemaining);
                setCurrentActivityId(res.data.activityId);
                setCustomerStatsByGame(prev => ({
                    ...prev,
                    [selectedGame!.name]: {
                        ...(prev[selectedGame!.name] || { totalPlaysToday: 0 }),
                        dailyPlaysRemaining: res.data.dailyPlaysRemaining
                    }
                }));
                setError('');
            }
        } catch (e) {
            console.warn('Failed to register play start:', e);
            setError('Failed to start game. Please try again.');
        } finally {
            setIsStarting(false);
        }
    };

    const handleGameStart = () => {
        console.log('🎮🎮🎮 HANDLE GAME START CALLED 🎮🎮🎮');
        console.log('🎮 Game starting - current plays remaining:', dailyPlaysRemaining);
        console.log('🆔 Current activity ID:', currentActivityId);
        console.log('🏃 isStarting:', isStarting);
        console.log('💾 isSubmittingScore:', isSubmittingScore);
        
        if (dailyPlaysRemaining <= 0) {
            console.log('❌ BLOCKED: No plays remaining today!');
            setError('No plays remaining today!');
            return;
        }
        // Prevent duplicate starts while a start is in progress
        if (isStarting) {
            console.log('❌ BLOCKED: Start already in progress, ignoring duplicate click');
            return;
        }

        // Prevent starting while score submission is in progress (race condition fix)
        if (isSubmittingScore) {
            console.log('❌ BLOCKED: Score submission in progress, cannot start new game yet');
            return;
        }

        // Note: Removed currentActivityId guard to allow "Play Again" to work
        // When Play Again is clicked, we want to start a fresh game even if an activity ID exists

        console.log('✅ All checks passed, starting game...');
        setIsStarting(true);

        // Clear any previous activity ID first
        console.log('🧹 Clearing previous activity ID before starting new game');
        setCurrentActivityId(null);

        // Register the play immediately so lives decrement even if score is 0
        (async () => {
            try {
                console.log('🚀 Calling backend startGame API for:', selectedGame!.name);
                const res = await startGame(selectedGame!.name);
                console.log('📡 Backend startGame response:', res);
                if (res && res.data) {
                    setDailyPlaysRemaining(res.data.dailyPlaysRemaining);
                    console.log('🆔 Setting new activity ID:', res.data.activityId);
                    setCurrentActivityId(res.data.activityId); // Store activity ID for later update
                    setCustomerStatsByGame(prev => ({
                        ...prev,
                        [selectedGame!.name]: {
                            ...(prev[selectedGame!.name] || { totalPlaysToday: 0 }),
                            dailyPlaysRemaining: res.data.dailyPlaysRemaining
                        }
                    }));
                    
                    // IMPORTANT: Only load the game AFTER we have the activity ID
                    // This ensures the activity ID is available when the game ends and score is submitted
                    if (!gameLoaded) {
                        console.log('🎮 Loading game for the first time - setting isInitialGameLoad flag');
                        setIsInitialGameLoad(true); // Mark that this is initial load, so handleGamePlayStart won't consume another life
                        setGameLoaded(true);
                    }
                    setError('');
                }
            } catch (e) {
                console.warn('Failed to register play start:', e);
                setError('Failed to start game. Please try again.');
            } finally {
                // allow future starts
                setIsStarting(false);
            }
        })();
            
        // Don't decrease here - will decrease after game ends with API response
    };



    const canPlayGame = () => {
        // Simply check if dailyPlaysRemaining > 0
        // This is updated from API responses and game card clicks
        return dailyPlaysRemaining > 0;
    };

    const getTimeUntilReset = () => {
        if (!selectedGame?.resetTime) return '';
        
        const now = new Date();
        const [hours, minutes] = selectedGame.resetTime.split(':');
        const resetToday = new Date();
        resetToday.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        // If reset time has passed today, set for tomorrow
        if (resetToday <= now) {
            resetToday.setDate(resetToday.getDate() + 1);
        }
        
        const diff = resetToday.getTime() - now.getTime();
        const hoursLeft = Math.floor(diff / (1000 * 60 * 60));
        const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        return `${hoursLeft}h ${minutesLeft}m`;
    };

    // Lives purchase handlers
    const handleBuyLives = (game: Game) => {
        setSelectedGameForPurchase(game);
        setShowLivesPurchaseModal(true);
    };

    // ...existing code...

    if (loading) {
        return (
            <div className="games-page bg-gray-50 min-h-screen flex flex-col">
                <CustomerTopNav />
                <div className="flex-1 flex justify-center items-center p-6">
                    <div className="flex flex-col items-center gap-4">
                        <div className="loading loading-dots loading-lg"></div>
                        <p className="text-gray-500">Loading games...</p>
                    </div>
                </div>
                <CustomerFooter />
            </div>
        );
    }

    return (
        <div className="games-page bg-gray-50 min-h-screen flex flex-col">
            <CustomerTopNav />
            
            <div className="flex-1 p-6">
                <div className="w-full max-w-6xl mx-auto">
                    {/* Games Header */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">Viva Games</h1>
                                <p className="text-gray-600">Play games and earn score!</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-xl">
                                    <div className="text-xs text-gray-600">Total Score</div>
                                    <div className="text-xl font-bold text-yellow-700">{totalScore}</div>
                                </div>
                                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center">
                                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.5a2.5 2.5 0 005 0H17m-8 3.5V14m4-3.5V14M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Games List */}
                        {games.length > 0 ? (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Games</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {games.map((game) => (
                                        <div
                                            key={game.id}
                                            onClick={() => {
                                                setSelectedGame(game);
                                                // Set dailyPlaysRemaining from our pre-fetched stats
                                                const gameStats = customerStatsByGame[game.name];
                                                if (gameStats) {
                                                    setDailyPlaysRemaining(gameStats.dailyPlaysRemaining);
                                                } else {
                                                    setDailyPlaysRemaining(game.maxPlayPerDay);
                                                }
                                            }}
                                            className={`cursor-pointer p-4 border-2 rounded-xl transition-all duration-200 ${
                                                selectedGame?.id === game.id
                                                    ? 'border-yellow-400 bg-yellow-50'
                                                    : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-25'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                {game.imageUrl ? (
                                                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                                                        <img
                                                            src={ensureHttps(game.imageUrl) || game.imageUrl}
                                                            alt={game.displayName}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                // hide broken image and fallback to gradient
                                                                const target = e.target as HTMLImageElement;
                                                                target.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.5a2.5 2.5 0 005 0H17m-8 3.5V14m4-3.5V14M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <div>
                                                    <h4 className="font-semibold text-gray-900">{game.displayName}</h4>
                                                    <p className="text-sm text-gray-500">Max {game.maxPlayPerDay} plays/day</p>
                                                </div>
                                            </div>
                                            {game.description && (
                                                <p className="text-sm text-gray-600 mb-3">{game.description}</p>
                                            )}
                                            <div className="flex justify-between items-center">
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    game.status === 'ACTIVE' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {game.status}
                                                </span>
                                                <div className="flex items-center gap-1 text-red-500">
                                                    <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                                                        <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                                                    </svg>
                                                    <span className="text-sm font-semibold">
                                                        {(() => {
                                                            const hasToken = !!SecureStorage.getCustomerToken();
                                                            const perGame = customerStatsByGame[game.name];
                                                            
                                                            if (selectedGame?.id === game.id) {
                                                                // Selected card: ALWAYS show dailyPlaysRemaining state
                                                                // This is the most up-to-date value from API responses
                                                                // Don't fallback to customerStatsByGame as it might be stale
                                                                return dailyPlaysRemaining;
                                                            }
                                                            
                                                            // Non-selected cards:
                                                            if (!hasToken) {
                                                                // Not logged in - show max plays
                                                                return game.maxPlayPerDay;
                                                            }
                                                            
                                                            if (!statsLoaded) {
                                                                // Still loading stats - show "..." placeholder
                                                                return '...';
                                                            }
                                                            
                                                            // Logged in and stats loaded
                                                            const remaining = perGame?.dailyPlaysRemaining;
                                                            console.log(`🔍 Card ${game.name}: perGame=`, perGame, `remaining=${remaining}`);
                                                            
                                                            // If we have stats for this game, use them (could be 0, 1, 2, 3, etc.)
                                                            if (remaining !== undefined) {
                                                                return remaining;
                                                            }
                                                            
                                                            // If stats loaded but no data for this game, assume max plays available
                                                            // (user has never played this game before)
                                                            console.log(`⚠️ No stats found for ${game.name}, using max: ${game.maxPlayPerDay}`);
                                                            return game.maxPlayPerDay;
                                                        })()}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            {/* Buy More Lives Button */}
                                            {SecureStorage.getCustomerToken() && game.allowLivesPurchase && (
                                                <div className="mt-3 pt-3 border-t border-gray-200">
                                                    <button
                                                        onClick={() => handleBuyLives(game)}
                                                        className="w-full px-3 py-2 text-sm bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-colors flex items-center justify-center gap-2"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                        </svg>
                                                        {(() => {
                                                            const g = normalizeGamePricing(game as any as Game);
                                                            const priceNum = typeof g.livesPrice === 'number' ? g.livesPrice : parseFloat(String(g.livesPrice));
                                                            const perUnit = typeof g.livesPerUnit === 'number' ? g.livesPerUnit : 0;
                                                            const symbol = currencySymbol(g.livesCurrency || 'AUD');
                                                            const priceFormatted = isFinite(priceNum) ? priceNum.toFixed(2) : '0.00';
                                                            return `Buy Extra Lives (${symbol}${priceFormatted} = ${perUnit} lives)`;
                                                        })()}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.5a2.5 2.5 0 005 0H17m-8 3.5V14m4-3.5V14M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Games Available</h3>
                                <p className="text-gray-500">No games have been added yet.</p>
                            </div>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-6">
                            {error}
                        </div>
                    )}

                            {/* Game Container */}
                            {selectedGame && (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                                    {gameLoaded ? (
                                        <>
                                            <div className="p-6 border-b border-gray-200">
                                                <div className="flex items-center justify-between mb-4">
                                                    <h2 className="text-xl font-bold text-gray-900">{selectedGame.displayName}</h2>
                                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                                </div>
                                                
                                                {/* Score Display */}
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="bg-yellow-50 p-3 rounded-lg">
                                                        <div className="text-sm text-gray-600">Current Score</div>
                                                        <div className="text-2xl font-bold text-yellow-600">{currentScore}</div>
                                                    </div>
                                                    <div className="bg-green-50 p-3 rounded-lg">
                                                        <div className="text-sm text-gray-600">Best Score</div>
                                                        <div className="text-2xl font-bold text-green-600">{bestScore}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="p-6 border-b border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <h2 className="text-xl font-bold text-gray-900">Select a Game</h2>
                                                <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                                            </div>
                                        </div>
                                    )}
                             
                            <div className="relative">
                                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center" style={{ height: 'calc(100vh)', minHeight: 800 }}>
                                    {!gameLoaded ? (
                                        <div className="text-center">
                                            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                                                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.5a2.5 2.5 0 005 0H17m-8 3.5V14m4-3.5V14M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{selectedGame.displayName}</h3>
                                            <p className="text-gray-500 mb-2">{selectedGame.description}</p>
                                            
                                            {/* Daily Plays Info */}
                                            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-medium text-gray-700">Plays Remaining Today:</span>
                                                    <span className={`text-lg font-bold ${dailyPlaysRemaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {(() => {
                                                            // If purchased lives exist for this game, show just remaining (they increase the total limit)
                                                            const perGameStats = customerStatsByGame[selectedGame!.name];
                                                            const purchased = perGameStats?.purchasedLives || 0;
                                                            if (purchased > 0) {
                                                                return `${dailyPlaysRemaining}`;
                                                            }
                                                            return `${dailyPlaysRemaining} / ${selectedGame.maxPlayPerDay}`;
                                                        })()}
                                                    </span>
                                                </div>
                                                {dailyPlaysRemaining === 0 && (
                                                    <div className="text-sm text-red-600">
                                                        Next reset in: {getTimeUntilReset()}
                                                    </div>
                                                )}
                                            </div>

                                            {canPlayGame() ? (
                                                <div>
                                                    <p className="text-sm text-gray-400 mb-6">
                                                        {isSubmittingScore ? 'Saving previous game...' : 
                                                         isStarting ? 'Starting game...' : 
                                                         'Ready to play? Click start to begin!'}
                                                    </p>
                                                    <button
                                                        onClick={handleGameStart}
                                                        disabled={isSubmittingScore || isStarting}
                                                        className={`px-8 py-3 rounded-lg font-medium text-lg transition-all duration-200 ${
                                                            isSubmittingScore || isStarting 
                                                                ? 'bg-gray-400 text-white cursor-not-allowed' 
                                                                : 'bg-gradient-to-r from-viva-purple to-viva-purple-light text-white hover:from-viva-purple-light hover:to-viva-purple'
                                                        }`}
                                                    >
                                                        {isSubmittingScore ? 'Saving...' : 
                                                         isStarting ? 'Starting...' : 
                                                         `Start ${selectedGame.displayName}`}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p className="text-sm text-red-600 mb-6">You've reached your daily play limit!</p>
                                                    <button
                                                        disabled
                                                        className="px-8 py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed font-medium text-lg"
                                                    >
                                                        Daily Limit Reached
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            {/* Once game is loaded, always show it regardless of remaining lives
                                                The life was already consumed when user clicked "Start Game" button */}
                                            <VivaClubGame
                                                dailyPlaysRemaining={dailyPlaysRemaining}
                                                onGameStart={async () => {
                                                    console.log('🎮 Game component requesting start - registering play only');
                                                    // For Play Again, we only need to register the play, not reload the game
                                                    await handleGamePlayStart();
                                                }}
                                                onGameEnd={(finalScore: number) => {
                                                    console.log('🎮 Game Over! Final score:', finalScore);
                                                    handleGameScore({ 
                                                        score: finalScore, 
                                                        level: 1, 
                                                        timestamp: new Date().toISOString() 
                                                    });
                                                    // Reset game loaded to show the start screen again
                                                    setGameLoaded(false);
                                                }}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Game Instructions */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mt-8">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">How to Play</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-3 bg-yellow-100 rounded-lg flex items-center justify-center">
                                    <span className="text-2xl">👆</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-2">Tap to Collect</h4>
                                <p className="text-sm text-gray-600">Tap on gold nuggets to collect them and increase your score</p>
                            </div>
                            <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-lg flex items-center justify-center">
                                    <span className="text-2xl">⚡</span>
                                </div>
                                <h4 className="font-semibold text.gray-900 mb-2">Winner</h4>
                                <p className="text-sm text-gray-600">Higher scores at end of month is winner</p>
                            </div>
                            <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <span className="text-2xl">🏆</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-2">Beat Your Best</h4>
                                <p className="text-sm text-gray-600">Challenge yourself to beat your personal best score</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Lives Purchase Modal */}
            {selectedGameForPurchase && (
                <LivesPurchaseModal
                    isOpen={showLivesPurchaseModal}
                    onClose={() => {
                        setShowLivesPurchaseModal(false);
                        setSelectedGameForPurchase(null);
                    }}
                    game={selectedGameForPurchase}
                />
            )}
            
            <CustomerFooter />
        </div>
    );
};

export default Games;
